import { Grid } from '@design-systems/canvas-ui'
import { BasicPlaceholder } from 'components/placeholder'
import { useAssetsProtectionSecureStore } from 'hooks/assets-protection/useAssetsProtectionSecureStore'
import { useEssentials } from 'hooks/utils/useEssentials'
import { Table } from 'components/table-compat/table-compat'
import { buildDataTableObject, TableData, Cell } from 'utils/data'
import { ErrorAlert } from 'components/error-fallback'
import useCountdown from 'hooks/utils/useCountdown'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { Container } from 'components/container/Container'

// Transform greenfield column-based data to row-based format
const transformGreenfieldData = (
    data: TableData | undefined,
    t: (key: string) => string,
    secure: Record<string, string>,
): TableData => {
    const firstRow = data?.rows?.[0] || []

    // Define metrics with their column indices and labels
    const metrics = [
        { label: t(secure['crimes against people']), indices: [1, 2] },
        { label: t(secure['crimes against property']), indices: [3, 4] },
        { label: t(secure['disorderly conducts']), indices: [7, 8] },
        { label: t(secure.securityResolution), indices: [5, 6] },
    ]

    // Create rows by picking columns for each metric
    const transformedRows: Cell[][] = metrics.map(({ label, indices }) => [
        { value: label },
        ...indices.map((idx) => firstRow[idx] || { value: '' }),
    ])

    return {
        headings: [
            { value: ' ' },
            { value: t(secure.ty) },
            { value: t(secure['b/(p) to ly']) },
        ],
        rows: transformedRows,
        footer: undefined,
    }
}

export const SecureStore = () => {
    const { t, currentLocation, env, session, logout, isEnglish } =
        useEssentials()
    const { secure } = TRANSLATION_KEYS.assetsProtection
    let { data, isLoading, isError, error, refetch } =
        useAssetsProtectionSecureStore({
            env,
            accessToken: session?.accessToken ?? '',
            location: currentLocation,
        })
    const count = useCountdown(5, logout, error as Error)

    const columnWidths = isEnglish ? [150, 75, 75] : [180, 75, 75]

    if (isLoading) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="table" rows={4} columns={3} />
            </div>
        )
    }
    if (isError) {
        return (
            <ErrorAlert error={error} count={count} onRetry={() => refetch()} />
        )
    }

    const transformedData = transformGreenfieldData(data, t, secure)

    return (
        <Container className="hc8-pt-4x">
            <Grid.Container className="hc8-pv-4x">
                <Grid.Item
                    className="hc8-pb-none"
                    size={{
                        xs: 12,
                    }}
                >
                    <p className="hc8-pa-1x hc8-lh-body">{t(secure.title)}</p>
                </Grid.Item>
                <Grid.Item
                    className="hc8-pt-none"
                    size={{
                        xs: 12,
                    }}
                >
                    <Table
                        data={buildDataTableObject({
                            currentData: transformedData,
                            widths: columnWidths,
                        })}
                        name={t(secure.title)}
                        scrollableWidth={true}
                    />
                </Grid.Item>
            </Grid.Container>
        </Container>
    )
}
